import React, { useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DownloadIcon from '@mui/icons-material/Download';
import TableViewIcon from '@mui/icons-material/TableView';
import { DataGridPro } from '@mui/x-data-grid-pro';

import { exportCsv, exportRawXlsx, exportXlsx } from './exportReportRows';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const isNumericValue = (v) => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'number') return Number.isFinite(v);
    if (typeof v === 'string') {
        const s = v.replace(',', '.').trim();
        if (s === '') return false;
        // Strings with leading zeros (e.g. "0218", "0300") are zero-padded codes, not numbers.
        if (s.length > 1 && /^0\d/.test(s)) return false;
        return !isNaN(Number(s));
    }
    return false;
};

const toNumber = (v) => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
        const s = v.replace(',', '.').trim();
        const n = Number(s);
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
};

// Column names that look numeric but are really identifiers / codes — we never sum these.
// Match is case-insensitive on the full column name OR a suffix after the last `.` or `_`.
const NON_AGGREGATABLE_NAMES = new Set([
    'id', 'idnr', 'rownum', 'rownr',
    'klnr', 'klantid', 'klant_id', 'klantnr', 'customerid', 'customer_id',
    'leverancier_id', 'leveranciersid', 'lev_id', 'levnr',
    'kassa_id', 'kassaid', 'pos_id', 'posid',
    'userid', 'user_id', 'gebruikerid',
    'magnr', 'mag', 'magid', 'warehouseid', 'warehouse_id',
    'barcode', 'sku', 'gtin', 'ean', 'upc',
    'postcode', 'postnr', 'zip', 'zipcode',
    'btw', 'vat', 'vatid', 'btwnr', 'btwnummer',
    'iban', 'bic',
    'gsm', 'tel', 'tel1', 'tel2', 'telefoon', 'phone', 'phonenr', 'phone_number', 'mobile',
    'fax', 'fax1',
    'ticketnr', 'orderid', 'order_id', 'ordernr', 'order_nr', 'invoicenr', 'invoice_nr', 'factuurnr',
    'repid', 'report_id',
    'code', 'totcode', 'aanspreking',
    'year', 'jaar', 'maand', 'month', 'day', 'dag', 'week',
    'seizoen', 'season',
]);

const isAggregatableColumnName = (col) => {
    if (!col) return false;
    const lowered = String(col).toLowerCase();
    if (NON_AGGREGATABLE_NAMES.has(lowered)) return false;
    // Try the leaf segment after the last "." or "_" (e.g. "klant.postcode" → "postcode",
    // "kasticket_.kassa_id" → "kassa_id" → "id").
    const afterDot = lowered.includes('.') ? lowered.slice(lowered.lastIndexOf('.') + 1) : lowered;
    if (NON_AGGREGATABLE_NAMES.has(afterDot)) return false;
    const afterUnderscore = afterDot.includes('_') ? afterDot.slice(afterDot.lastIndexOf('_') + 1) : afterDot;
    if (NON_AGGREGATABLE_NAMES.has(afterUnderscore)) return false;
    // Common suffix patterns: anything ending in "_id", "_nr", "_code", "_postcode", "_zip".
    if (/(_id|_nr|_code|_postcode|_zip)$/.test(lowered)) return false;
    // Code-style columns: code1..code10, klcode1..klcode10, c1oms..c10oms (description columns
    // are pure text anyway but we exclude here for safety in case the values look numeric).
    if (/^k?l?code\d{1,2}$/.test(afterDot)) return false;
    if (/^c\d{1,2}oms$/.test(afterDot)) return false;
    // Percentage columns: summing percentages is almost always wrong — exclude any column
    // whose name contains "pct", "percent", or "percentage" anywhere (case-insensitive),
    // including camelCase forms like `pctBw`, `pctGele`, `pctVerk`, `totalPercentage`, etc.
    if (/(pct|percent|percentage)/i.test(lowered)) return false;
    // Supplier-article reference columns: `levart`, `levart1`, `levart2`, … are supplier-side
    // article identifiers, not quantities — never sum them.
    if (/^levart\d*$/.test(afterDot)) return false;
    return true;
};

const detectNumericColumns = (columnNames, rows, sampleSize = 50) => {
    const numericSet = new Set();
    const sample = rows.slice(0, sampleSize);
    columnNames.forEach((col) => {
        if (!isAggregatableColumnName(col)) return;
        let numericHits = 0;
        let nonEmpty = 0;
        for (const row of sample) {
            const val = row?.[col];
            if (val === null || val === undefined || val === '') continue;
            nonEmpty += 1;
            if (isNumericValue(val)) numericHits += 1;
        }
        if (nonEmpty > 0 && numericHits / nonEmpty >= 0.8) {
            numericSet.add(col);
        }
    });
    return numericSet;
};

// Detect a yyyy-mm-dd value (optionally followed by a time) and reformat to dd/mm/yyyy.
// Returns null when the value isn't a recognisable ISO-like date string, so the caller can
// fall through to its normal string rendering.
const DATE_ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const formatDateIfIso = (value) => {
    if (typeof value !== 'string') return null;
    const m = DATE_ISO_RE.exec(value.trim());
    if (!m) return null;
    const [, y, mo, d, time] = m;
    // Reject obviously bogus zero dates like "0000-00-00" — render as empty rather than "00/00/0000".
    if (y === '0000' || mo === '00' || d === '00') return '';
    return time ? `${d}/${mo}/${y} ${time}` : `${d}/${mo}/${y}`;
};

// Belgian formatting using fr-BE: thin space (U+202F) as thousands separator and "," as decimal.
// We always use fr-BE regardless of UI language so numbers render consistently as "4 590,12"
// instead of the nl-BE "4.590,12" form which is easy to mistake for a decimal.
const formatNumberForDisplay = (n, fixedDecimals) => {
    if (n === null || n === undefined || !Number.isFinite(n)) return '';
    const hasFixed = typeof fixedDecimals === 'number';
    const minD = hasFixed ? fixedDecimals : (Number.isInteger(n) ? 0 : 2);
    const maxD = hasFixed ? fixedDecimals : 2;
    return n.toLocaleString('fr-BE', {
        minimumFractionDigits: minD,
        maximumFractionDigits: maxD,
    });
};

// Known id→description column pairs from SoftTouch SQL conventions. When grouping by a column
// in this map, we look for a same-bucket sibling among its candidate description columns; if
// found and consistent across all rows in the bucket, we use it as the label instead of the
// raw id. For any column NOT in this map (e.g. postcode, gemeente), we just show the literal
// group-key value — that prevents picking up coincidentally-consistent siblings like firstname.
const ID_TO_DESCRIPTION_PAIRS = (() => {
    const m = {
        levnr:        ['levnaam'],
        seizoen:      ['seizoen_oms', 'seiz_oms', 'oms'],
        codekleur:    ['kleuroms', 'kleur_oms'],
        mag:          ['magoms', 'mag_oms'],
        kassa_id:     ['kassa_oms', 'oms'],
        aanspreking:  ['aansprekingoms', 'codeoms'],
        userid:       ['naam'],
    };
    for (let i = 1; i <= 10; i += 1) {
        m[`code${i}`]   = [`c${i}oms`];
        m[`klcode${i}`] = [`klc${i}oms`, `klcode${i}oms`];
    }
    return m;
})();

// For a clustered group, find a description sibling for the group column. Only triggers for
// columns that have a known id→description mapping; otherwise returns null and the caller
// falls back to the literal group-key value.
const findGroupDescription = (bucket, columnNames, groupBy) => {
    if (!Array.isArray(bucket) || bucket.length === 0) return null;
    const leaf = (() => {
        const s = String(groupBy || '').toLowerCase();
        const afterDot = s.includes('.') ? s.slice(s.lastIndexOf('.') + 1) : s;
        return afterDot;
    })();
    const candidates = ID_TO_DESCRIPTION_PAIRS[leaf];
    if (!candidates || candidates.length === 0) return null;

    const colByLower = new Map();
    columnNames.forEach(c => colByLower.set(String(c).toLowerCase(), c));

    for (const cand of candidates) {
        const realName = colByLower.get(cand);
        if (!realName || realName === groupBy) continue;
        const first = bucket[0]?.[realName];
        if (first === null || first === undefined || first === '') continue;
        if (typeof first !== 'string') continue;
        let consistent = true;
        for (let i = 1; i < bucket.length; i += 1) {
            if (bucket[i]?.[realName] !== first) { consistent = false; break; }
        }
        if (consistent) return first;
    }
    return null;
};

const SUBTOTAL_PREFIX     = '__sub__';
const GROUP_HEADER_PREFIX = '__hdr__';
const GRAND_TOTAL_ID      = '__grand_total__';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const ReportResultGrid = (props) => {
    const { columns: columnNames = [], rows: rawRows = [], truncated, rowCount, t, reportTitle, layout = null } = props;

    const [searchTerm, setSearchTerm] = useState('');
    // Auto-apply grouping from the FR3 layout (if any); still fully editable afterwards.
    const [groupBy, setGroupBy]       = useState(() =>
        (layout?.groups || []).map(g => g.key).filter(k => (columnNames || []).includes(k))
    );
    const [pageSize, setPageSize]     = useState(50);

    // ── Numeric column detection (once per row set) ─────────────────────────
    const numericColumns = useMemo(
        () => detectNumericColumns(columnNames, rawRows),
        [columnNames, rawRows]
    );

    // ── Layout (FR3) derived maps ───────────────────────────────────────────
    const layoutCols = useMemo(() => (layout?.columns || []), [layout]);
    const hasLayoutColumns = layoutCols.length > 0;
    const colMeta = useMemo(() => {
        const m = {};
        layoutCols.forEach(c => { m[c.field] = c; });
        return m;
    }, [layoutCols]);
    // Visible columns + order: FR3 column order when available, else the raw result columns.
    // (Helper/key columns not shown by the FR3 stay in the row data for grouping/sums, just hidden.)
    const orderedFields = useMemo(() => {
        if (hasLayoutColumns) {
            // Keep computed (ratio) columns too — their synthetic field isn't a SQL column.
            const inLayout = layoutCols.filter(c => c.computed || columnNames.includes(c.field)).map(c => c.field);
            if (inLayout.length) return inLayout;
        }
        return columnNames;
    }, [layoutCols, hasLayoutColumns, columnNames]);
    const groupLabelField = useMemo(() => {
        const m = {};
        (layout?.groups || []).forEach(g => { if (g.labelField) m[g.key] = g.labelField; });
        return m;
    }, [layout]);
    const hasLayout = hasLayoutColumns || (layout?.groups?.length > 0) || (layout?.totals?.length > 0);
    const labelColumn = orderedFields[0] || columnNames[0];

    // Columns we SUM (subtotals + grand total). The FR3 says exactly which columns are totalled
    // (so percentage/ratio columns are never summed); otherwise fall back to numeric detection.
    const sumColumns = useMemo(() => {
        if (layout && Array.isArray(layout.totals) && layout.totals.length) {
            return new Set(layout.totals.filter(c => columnNames.includes(c)));
        }
        return numericColumns;
    }, [layout, columnNames, numericColumns]);

    // Columns rendered as numbers (right-aligned + formatted) — superset of the summed ones.
    const displayNumeric = useMemo(() => {
        const s = new Set(numericColumns);
        sumColumns.forEach(c => s.add(c));
        // Computed (ratio) columns are numeric by definition. We intentionally do NOT promote a
        // column to numeric just because the FR3 gave it a numeric DisplayFormat — some text cells
        // (e.g. weather_description) carry a stray "%2.2f" format and must stay text, otherwise
        // toNumber("Zwaar bewolkt") = 0 renders as "0.00". Numeric-ness is driven by the data.
        Object.values(colMeta).forEach(c => { if (c.computed) s.add(c.field); });
        return s;
    }, [numericColumns, sumColumns, colMeta]);

    // ── Filter ──────────────────────────────────────────────────────────────
    const filteredRows = useMemo(() => {
        if (!searchTerm) return rawRows;
        const needle = searchTerm.toLowerCase();
        return rawRows.filter(row =>
            columnNames.some(col => {
                const v = row?.[col];
                if (v === null || v === undefined) return false;
                return String(v).toLowerCase().includes(needle);
            })
        );
    }, [rawRows, columnNames, searchTerm]);

    // ── Group + subtotal injection ──────────────────────────────────────────
    // displayRows: the rows DataGrid actually renders inline (data + per-group subtotals).
    // pinnedBottomRow: the grand-total row pinned at the bottom (always visible regardless of page).
    //
    // Multi-level grouping: `groupBy` is an ordered list of column names. Rows are sorted
    // lexicographically over all keys, then we emit subtotals on the boundary between groups,
    // deepest first (innermost level subtotal comes before the outer-level subtotal that
    // contains it).
    const { displayRows, pinnedBottomRow } = useMemo(() => {
        // Always attach a stable id (DataGridPro requires unique ids).
        const baseRows = filteredRows.map((row, idx) => ({ ...row, __id__: `r_${idx}` }));

        const grandTotalRow = () => {
            if (sumColumns.size === 0 || baseRows.length === 0) return null;
            const sums = computeSums(baseRows, sumColumns);
            const labelCol = hasLayout ? labelColumn : ((groupBy[0]) || columnNames[0]);
            return {
                __id__: GRAND_TOTAL_ID,
                __type__: 'grand_total',
                ...emptyRowShape(columnNames),
                [labelCol]: t('reports_grandTotal') || 'Grand total',
                ...sums,
            };
        };

        if (!groupBy || groupBy.length === 0) {
            return { displayRows: baseRows, pinnedBottomRow: grandTotalRow() };
        }

        // Sort by the full group-key tuple (level 0 first, then level 1, ...).
        // When a level groups by levnr, order it by the supplier name (levnaam) instead of the
        // raw id, so the visible order matches the group labels. levnaam is constant within a
        // levnr, so grouping stays contiguous; boundary detection below still keys off levnr.
        const colByLower = new Map(columnNames.map(c => [String(c).toLowerCase(), c]));
        const sortCols = groupBy.map((col) => {
            const s = String(col || '').toLowerCase();
            const leaf = s.includes('.') ? s.slice(s.lastIndexOf('.') + 1) : s;
            if (leaf === 'levnr') return colByLower.get('levnaam') || col;
            return col;
        });
        const sorted = [...baseRows].sort((a, b) => {
            for (const col of sortCols) {
                const av = a?.[col] ?? '';
                const bv = b?.[col] ?? '';
                if (av < bv) return -1;
                if (av > bv) return 1;
            }
            return 0;
        });

        const out = [];
        const depth = groupBy.length;
        const levelRows  = groupBy.map(() => []);  // accumulated rows since each level last started
        const levelKey   = groupBy.map(() => null);
        let initialized = false;
        let subtotalIdx = 0;
        let headerIdx   = 0;

        const labelForLevel = (level, sampleBucket) => {
            const col       = groupBy[level];
            const keyValue  = levelKey[level];
            const literal   = keyValue != null && keyValue !== '' ? String(keyValue) : '—';
            // FR3 group label field (e.g. group on `seizoen`, show `seizOms`).
            const lf = groupLabelField[col];
            if (lf && Array.isArray(sampleBucket) && sampleBucket.length) {
                const v = sampleBucket[0]?.[lf];
                if (v != null && v !== '') return String(v);
            }
            const description = findGroupDescription(sampleBucket, columnNames, col);
            return description != null ? description : literal;
        };

        const emitHeader = (level, seedRow) => {
            const col   = groupBy[level];
            const label = labelForLevel(level, [seedRow]);
            out.push({
                __id__: `${GROUP_HEADER_PREFIX}${headerIdx}`,
                __type__: 'group_header',
                __depth__: level,
                ...emptyRowShape(columnNames),
                [hasLayout ? labelColumn : col]: label,
            });
            headerIdx += 1;
        };

        const emitSubtotal = (level) => {
            const bucket = levelRows[level];
            if (!bucket.length) return;
            const sums  = computeSums(bucket, sumColumns);
            const col   = groupBy[level];
            const label = labelForLevel(level, bucket);
            out.push({
                __id__: `${SUBTOTAL_PREFIX}${subtotalIdx}`,
                __type__: 'subtotal',
                __depth__: level,
                ...emptyRowShape(columnNames),
                [hasLayout ? labelColumn : col]: `${t('reports_groupTotal') || 'Subtotal'}: ${label}`,
                ...sums,
            });
            subtotalIdx += 1;
        };

        for (const row of sorted) {
            const currKeys = groupBy.map(c => row?.[c] ?? '');

            if (initialized) {
                // Find shallowest level whose key changed.
                let changedAt = depth;
                for (let i = 0; i < depth; i += 1) {
                    if (currKeys[i] !== levelKey[i]) { changedAt = i; break; }
                }
                // Flush subtotals for levels [changedAt..depth-1], deepest first.
                for (let i = depth - 1; i >= changedAt; i -= 1) {
                    emitSubtotal(i);
                    levelRows[i] = [];
                    levelKey[i]  = currKeys[i];
                }
                // Emit headers for the newly-starting groups, outermost first.
                for (let i = changedAt; i < depth; i += 1) {
                    emitHeader(i, row);
                }
            } else {
                for (let i = 0; i < depth; i += 1) levelKey[i] = currKeys[i];
                for (let i = 0; i < depth; i += 1) emitHeader(i, row);
                initialized = true;
            }

            for (let i = 0; i < depth; i += 1) levelRows[i].push(row);
            out.push(row);
        }

        if (initialized) {
            for (let i = depth - 1; i >= 0; i -= 1) emitSubtotal(i);
        }

        return { displayRows: out, pinnedBottomRow: grandTotalRow() };
    }, [filteredRows, groupBy, numericColumns, sumColumns, columnNames, t, hasLayout, labelColumn, groupLabelField]);

    // ── DataGrid columns ────────────────────────────────────────────────────
    const gridColumns = useMemo(() => {
        return orderedFields.map((col) => {
            const meta = colMeta[col];
            const computed = meta && meta.computed;
            const isNumeric = displayNumeric.has(col);
            const align = (meta && meta.align) ? meta.align : (isNumeric ? 'right' : 'left');
            // A layout column with no FR3 caption (e.g. a column that sat under a wider, merged
            // header in the original report) renders with a blank header rather than the raw SQL
            // column name. Non-layout columns still fall back to the field name.
            const headerName = meta ? (meta.caption || '') : col;
            const decimals = (meta && typeof meta.decimals === 'number') ? meta.decimals : undefined;
            const suffix = (computed && meta.computed.percent) ? '%' : '';
            const hideZeros = !!(meta && meta.hideZeros);
            return {
                field: col,
                headerName,
                flex: 1,
                minWidth: 120,
                align,
                headerAlign: align,
                // Manual column sorting is disabled while grouping is active: it would reorder the
                // injected group-header/subtotal rows and break the grouped layout. Setting this
                // false also neutralises any sort model left over from before grouping was enabled.
                sortable: groupBy.length === 0,
                // v7 signature: (value, row, column, apiRef) — not the v6 `params` object.
                valueGetter: (_value, row) => cellValueForColumn(meta, col, row),
                renderCell: (params) => {
                    const rowType = params.row.__type__;
                    if (rowType === 'group_header') {
                        // Header rows only carry the group label (in the first column); never compute.
                        return <strong>{params.row?.[col] ?? ''}</strong>;
                    }
                    const value = cellValueForColumn(meta, col, params.row);
                    if (rowType === 'subtotal' || rowType === 'grand_total') {
                        if (isNumeric && typeof value === 'number') {
                            if (hideZeros && value === 0) return <strong></strong>;
                            return <strong>{formatNumberForDisplay(value, decimals)}{suffix}</strong>;
                        }
                        return <strong>{value ?? ''}</strong>;
                    }
                    if (isNumeric && value !== null && value !== undefined && value !== '') {
                        const num = toNumber(value);
                        if (hideZeros && num === 0) return '';
                        return formatNumberForDisplay(num, decimals) + suffix;
                    }
                    if (value === null || value === undefined) return '';
                    const dateFormatted = formatDateIfIso(value);
                    if (dateFormatted !== null) return dateFormatted;
                    return String(value);
                },
            };
        });
    }, [orderedFields, colMeta, displayNumeric, groupBy]);

    const exportFilename = (reportTitle || 'report')
        .replace(/[^a-z0-9-_]+/gi, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 60) || 'report';

    // CSV gets pure data rows. XLSX gets the full visible grid (data + group headers +
    // subtotals + pinned grand total) so the export mirrors what's on screen.
    const csvExportRows = filteredRows;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400, gap: 1.5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('reports_searchResults') || 'Search results…'}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                    sx={{ minWidth: 220, flex: 1 }}
                />
                <Autocomplete
                    multiple
                    size="small"
                    options={columnNames}
                    value={groupBy}
                    onChange={(_, newVal) => setGroupBy(Array.isArray(newVal) ? newVal : [])}
                    getOptionLabel={(opt) => String(opt)}
                    renderTags={(values, getTagProps) =>
                        values.map((option, index) => (
                            <Chip
                                size="small"
                                label={`${index + 1}. ${option}`}
                                {...getTagProps({ index })}
                                key={option}
                            />
                        ))
                    }
                    sx={{ minWidth: 280, flex: 1 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('reports_groupBy') || 'Group by'}
                            placeholder={groupBy.length ? '' : (t('reports_groupAll') || 'No grouping')}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <InputAdornment position="start"><GroupWorkIcon fontSize="small" /></InputAdornment>
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                <Box sx={{ flex: 1 }} />
                <Tooltip title={t('reports_exportCsv') || 'Export CSV'}>
                    <span>
                        <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => exportCsv(orderedFields, csvExportRows, exportFilename)}
                            disabled={csvExportRows.length === 0}
                        >
                            CSV
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={t('reports_exportXlsx') || 'Export XLSX'}>
                    <span>
                        <Button
                            size="small"
                            startIcon={<TableViewIcon />}
                            onClick={() => exportXlsx(orderedFields, displayRows, exportFilename, { numericColumns: displayNumeric, pinnedBottomRow })}
                            disabled={displayRows.length === 0}
                        >
                            XLSX
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={(t('reports_exportXlsxRawTip') && t('reports_exportXlsxRawTip') !== 'reports_exportXlsxRawTip') ? t('reports_exportXlsxRawTip') : 'Alle rijen van de rapportquery, zonder layout of groepering'}>
                    <span>
                        <Button
                            size="small"
                            startIcon={<TableViewIcon />}
                            onClick={() => exportRawXlsx(columnNames, rawRows, exportFilename)}
                            disabled={rawRows.length === 0}
                        >
                            {(t('reports_exportXlsxRaw') && t('reports_exportXlsxRaw') !== 'reports_exportXlsxRaw') ? t('reports_exportXlsxRaw') : 'XLSX - ruwe data'}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={`${(t('reports_rowCount') || 'Rows')}: ${rowCount ?? rawRows.length}`} />
                {searchTerm && (
                    <Chip size="small" color="info"
                          label={`${(t('reports_filteredRows') || 'Filtered')}: ${filteredRows.length}`} />
                )}
                {truncated && (
                    <Chip size="small" color="warning"
                          label={t('reports_truncated') || 'Result truncated to 50 000 rows'} />
                )}
            </Stack>

            <Box sx={{ flex: 1, minHeight: 300 }}>
                <DataGridPro
                    className={props.theme}
                    rows={displayRows}
                    columns={gridColumns}
                    getRowId={(row) => row.__id__}
                    pinnedRows={pinnedBottomRow ? { bottom: [pinnedBottomRow] } : undefined}
                    getRowClassName={(params) => {
                        if (params.row.__type__ === 'grand_total') return 'reports-grand-total-row';
                        if (params.row.__type__ === 'subtotal') {
                            const d = typeof params.row.__depth__ === 'number' ? params.row.__depth__ : 0;
                            return `reports-subtotal-row reports-subtotal-row-depth-${d}`;
                        }
                        if (params.row.__type__ === 'group_header') {
                            const d = typeof params.row.__depth__ === 'number' ? params.row.__depth__ : 0;
                            return `reports-group-header-row reports-group-header-row-depth-${d}`;
                        }
                        return '';
                    }}
                    density="compact"
                    disableRowSelectionOnClick
                    disableColumnSort={groupBy.length > 0}
                    pagination
                    pageSizeOptions={[25, 50, 100, 250]}
                    pageSize={pageSize}
                    onPageSizeChange={(s) => setPageSize(s)}
                    sx={{
                        '& .reports-group-header-row':         { backgroundColor: 'rgba(34, 197, 94, 0.10)', fontWeight: 600 },
                        '& .reports-group-header-row-depth-0': { backgroundColor: 'rgba(34, 197, 94, 0.20)', fontWeight: 700 },
                        '& .reports-group-header-row-depth-1': { backgroundColor: 'rgba(34, 197, 94, 0.15)', fontWeight: 700 },
                        '& .reports-group-header-row-depth-2': { backgroundColor: 'rgba(34, 197, 94, 0.12)', fontWeight: 600 },
                        '& .reports-subtotal-row':             { backgroundColor: 'rgba(99, 102, 241, 0.08)', fontWeight: 600 },
                        '& .reports-subtotal-row-depth-0':     { backgroundColor: 'rgba(99, 102, 241, 0.18)', fontWeight: 700 },
                        '& .reports-subtotal-row-depth-1':     { backgroundColor: 'rgba(99, 102, 241, 0.14)', fontWeight: 700 },
                        '& .reports-subtotal-row-depth-2':     { backgroundColor: 'rgba(99, 102, 241, 0.10)', fontWeight: 600 },
                        '& .reports-grand-total-row':          { backgroundColor: 'rgba(239, 68, 68, 0.08)', fontWeight: 700 },
                        '& .MuiDataGrid-pinnedRows':           { backgroundColor: 'rgba(239, 68, 68, 0.10)', fontWeight: 700 },
                    }}
                />
            </Box>
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers (kept at bottom for readability)
// ─────────────────────────────────────────────────────────────────────────────

// Value for a layout column on a given row. Computed (ratio) columns are derived from their
// numerator/denominator fields — which works for detail rows AND subtotal/total rows, since the
// latter already carry the summed components (mirrors the FR3 footers' SUM(num)/SUM(den)).
// Returns null when the denominator is 0 (undefined ratio → rendered blank).
function cellValueForColumn(meta, col, row) {
    if (meta && meta.computed) {
        const den = toNumber(row?.[meta.computed.den]);
        if (!den) return null;
        return (toNumber(row?.[meta.computed.num]) / den) * (meta.computed.scale || 1);
    }
    return row?.[col];
}

function computeSums(rows, numericColumns) {
    const sums = {};
    numericColumns.forEach(col => { sums[col] = 0; });
    for (const row of rows) {
        numericColumns.forEach(col => {
            const v = row?.[col];
            if (v !== null && v !== undefined && v !== '') sums[col] += toNumber(v);
        });
    }
    return sums;
}

function emptyRowShape(columnNames) {
    const r = {};
    columnNames.forEach(c => { r[c] = ''; });
    return r;
}

export default ReportResultGrid;
